import { IconEdit, IconTrash } from "@tabler/icons-react";
import { AdvancedConfirmDialog, NoData, Pagination, TableContainer } from "components";
import { LangConstant } from "const";
import { useChannelDelete } from "hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemUtils } from "utils";
import UpdateChannelDialog from "./UpdateChannelDialog";

const VIEW_MODE = {
  edit: "edit",
  delete: "delete",
  batchDelete: "batchDelete",
};

const ChannelListTable = ({ channels, totalChannels, page, totalPages, isFetching, onChangePage, groupType }) => {
  const { t: getLabel } = useTranslation();
  const { t: getCommonLabel } = useTranslation(LangConstant.NS_COMMON);

  const isChannel = groupType === "channel";

  const { deleteChannel } = useChannelDelete();

  const tableLabels = getLabel(LangConstant.OBJ_CHANNEL_LIST_TABLE, { returnObjects: true });

  const [viewMode, setViewMode] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const columns = useMemo(
    () => [
      { label: "", align: "center", width: "40px" },
      { label: tableLabels.channelName, align: "left" },
      { label: tableLabels.memberCount, align: "left", width: "20%" },
      { label: tableLabels.action, align: "center", width: "12%" },
    ],
    [tableLabels],
  );

  const isAllSelected = channels.length > 0 && channels.every(ch => selectedIds.includes(ch.id));

  const handleToggleAll = () => {
    if (isAllSelected) {
      setSelectedIds(prev => prev.filter(id => !channels.some(ch => ch.id === id)));
    } else {
      const currentPageIds = channels.map(ch => ch.id);
      setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleToggleOne = channelId => {
    setSelectedIds(prev => (prev.includes(channelId) ? prev.filter(id => id !== channelId) : [...prev, channelId]));
  };

  const handleDeleteMode = channel => () => {
    setSelectedChannel(channel);
    setViewMode(VIEW_MODE.delete);
  };

  const handleEditMode = channel => () => {
    setSelectedChannel(channel);
    setViewMode(VIEW_MODE.edit);
  };

  const handleBatchDeleteMode = () => {
    setViewMode(VIEW_MODE.batchDelete);
  };

  const handleReset = () => {
    setViewMode(null);
    setSelectedChannel(null);
  };

  const handleConfirmDelete = () => {
    deleteChannel({
      groupId: selectedChannel.id,
      onSuccess: () => {
        setSelectedIds(prev => prev.filter(id => id !== selectedChannel.id));
        handleReset();
      },
    });
  };

  const handleConfirmBatchDelete = () => {
    const idsToDelete = [...selectedIds];
    let completed = 0;
    let successCount = 0;
    const total = idsToDelete.length;

    const checkDone = () => {
      completed++;
      if (completed >= total) {
        if (successCount > 0) {
          SystemUtils.appendNotification(
            getLabel("FM_DISSOLVE_BATCH_SUCCESS", { number: successCount }),
            "success",
          );
        }
        if (successCount < total) {
          SystemUtils.appendNotification(
            getLabel("FM_DISSOLVE_BATCH_FAIL", { number: total - successCount }),
            "error",
          );
        }
        setSelectedIds([]);
        handleReset();
      }
    };

    idsToDelete.forEach(groupId => {
      deleteChannel({
        groupId,
        silent: true,
        skipLoading: true,
        onSuccess: () => { successCount++; },
        onFinally: checkDone,
      });
    });
  };

  const handlePageClick = newPage => {
    onChangePage(null, newPage);
  };

  return (
    <div className="d-flex flex-column gap-3" style={{ flex: 1, minHeight: 0 }}>
      <div className="d-flex align-items-center justify-content-between">
        <strong style={{ fontSize: 20 }}>
        {isChannel
          ? getLabel(LangConstant.FM_TOTAL_CHANNELS, { number: totalChannels })
          : (getLabel("FM_TOTAL_GROUPS", { number: totalChannels }) || `Tổng số nhóm: ${totalChannels}`)}
      </strong>

        {selectedIds.length > 0 && (
          <button className="btn btn-danger" onClick={handleBatchDeleteMode}>
            <IconTrash size={16} className="me-1" />
            {getLabel("TXT_DISSOLVE_SELECTED", { number: selectedIds.length })}
          </button>
        )}
      </div>

      <TableContainer style={{ minHeight: "450px" }}>
        <table className="table table-vcenter table-hover">
          <thead>
            <tr>
              <th className="text-center" style={{ width: 40 }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isAllSelected}
                  onChange={handleToggleAll}
                  disabled={channels.length === 0}
                />
              </th>
              {columns.slice(1).map((col, index) => (
                <th key={index} className={`text-${col.align || "start"}`} style={{ width: col.width }}>
                  <strong>{col.label}</strong>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channels.map(channel => (
              <tr key={channel.id} className={selectedIds.includes(channel.id) ? "table-active" : ""}>
                <td className="text-center" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedIds.includes(channel.id)}
                    onChange={() => handleToggleOne(channel.id)}
                  />
                </td>
                <td className="text-left">{channel.groupName}</td>
                <td className="text-left">{channel.memberCount}</td>
                <td className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <button
                      className="btn btn-icon btn-ghost-primary"
                      style={{ width: 32, height: 32 }}
                      title={getLabel("TXT_EDIT_INFO")}
                      onClick={handleEditMode(channel)}
                    >
                      <IconEdit size={18} />
                    </button>
                    <button
                      className="btn btn-icon btn-ghost-danger"
                      style={{ width: 32, height: 32 }}
                      title={isChannel ? getLabel("TXT_DISSOLVE_CHANNEL") : getLabel("TXT_DISSOLVE_GROUP")}
                      onClick={handleDeleteMode(channel)}
                    >
                      <IconTrash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isFetching && channels.length === 0 && <NoData content={getCommonLabel("TXT_NO_CHANNEL")} />}
      </TableContainer>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={(e, p) => handlePageClick(p)} />

      {viewMode === VIEW_MODE.delete && selectedChannel && (
        <AdvancedConfirmDialog
          open
          title={isChannel ? getCommonLabel("TXT_DISSOLVE_CHANNEL") : getCommonLabel("TXT_DISSOLVE_GROUP")}
          content={isChannel
            ? getCommonLabel("FM_DISSOLVE_CHANNEL_CONTENT", { groupName: selectedChannel.groupName })
            : getCommonLabel("FM_DISSOLVE_GROUP_CONTENT", { groupName: selectedChannel.groupName })}
          submitProps={{
            submitText: getCommonLabel("L_CONFIRM_BUTTON"),
            onClick: handleConfirmDelete,
          }}
          cancelProps={{
            onClick: handleReset,
          }}
        />
      )}

      {viewMode === VIEW_MODE.batchDelete && (
        <AdvancedConfirmDialog
          open
          title={isChannel ? getLabel("TXT_DISSOLVE_CHANNEL") : getLabel("TXT_DISSOLVE_GROUP")}
          content={getLabel("FM_DISSOLVE_BATCH_CONTENT", { number: selectedIds.length })}
          submitProps={{
            submitText: getCommonLabel("L_CONFIRM_BUTTON"),
            onClick: handleConfirmBatchDelete,
          }}
          cancelProps={{
            onClick: handleReset,
          }}
        />
      )}

      {viewMode === VIEW_MODE.edit && selectedChannel && (
        <UpdateChannelDialog open channel={selectedChannel} onClose={handleReset} />
      )}
    </div>
  );
};

export default ChannelListTable;
